import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { listChatThreads, getChatThread, createChatThread, streamChat, deleteChatThread, updateChatThread } from '../lib/api'
import { cn, formatDate } from '../lib/utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  Send,
  Plus,
  History,
  Loader2,
  Sparkles,
  User,
  Trash2,
  X,
  MessageSquare,
  ChevronDown,
  Paperclip,
  Pencil,
  Check,
} from 'lucide-react'
import { AttachedContents, ContentPickerModal } from '../components/ContentPicker'

function ChatMessage({ message, isUser }) {
  return (
    <div className={cn(
      "flex gap-3 animate-fade-in",
      isUser ? "flex-row-reverse" : ""
    )}>
      <div className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
        isUser ? "bg-primary" : "bg-gradient-to-br from-primary to-purple-500"
      )}>
        {isUser ? (
          <User className="w-4 h-4 text-primary-foreground" />
        ) : (
          <Sparkles className="w-4 h-4 text-white" />
        )}
      </div>
      <div className={cn(
        "max-w-[80%] rounded-2xl px-4 py-3",
        isUser
          ? "bg-primary text-primary-foreground rounded-tr-sm"
          : "bg-card border border-border rounded-tl-sm"
      )}>
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  )
}

function ThreadItem({ thread, isActive, onClick, onDelete }) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all group cursor-pointer",
        isActive
          ? "bg-primary/10 border border-primary/30"
          : "hover:bg-accent border border-transparent"
      )}
    >
      <MessageSquare className={cn(
        "w-4 h-4 flex-shrink-0",
        isActive ? "text-primary" : "text-muted-foreground"
      )} />
      <div className="flex-1 min-w-0">
        <p className={cn(
          "text-sm font-medium truncate",
          isActive && "text-primary"
        )}>
          {thread.name}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatDate(thread.updated_at || thread.updatedAt)}
        </p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onDelete(thread.id)
        }}
        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 hover:text-destructive transition-all"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

export default function ChatPage() {
  const { threadId } = useParams()
  const navigate = useNavigate()
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  const [threads, setThreads] = useState([])
  const [currentThread, setCurrentThread] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [attachedContents, setAttachedContents] = useState([])
  const [showContentPicker, setShowContentPicker] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')

  useEffect(() => {
    loadThreads()
  }, [])

  useEffect(() => {
    if (threadId) {
      loadThread(threadId)
    } else {
      setCurrentThread(null)
      setMessages([])
      setAttachedContents([])
    }
  }, [threadId])

  useEffect(() => {
    scrollToBottom()
  }, [messages, streamingContent])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const loadThreads = async () => {
    try {
      const res = await listChatThreads()
      setThreads(res.threads || [])
    } catch (err) {
      console.error('Failed to load threads:', err)
    }
  }

  const loadThread = async (id) => {
    setLoading(true)
    try {
      const res = await getChatThread(id)
      const thread = res.thread
      setCurrentThread(thread)
      
      const loadedMessages = []
      for (const h of (thread.history || [])) {
        loadedMessages.push({
          id: `q-${h.id}`,
          content: h.question,
          isUser: true,
        })
        loadedMessages.push({
          id: `a-${h.id}`,
          content: h.answer,
          isUser: false,
        })
      }
      setMessages(loadedMessages)
    } catch (err) {
      console.error('Failed to load thread:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async () => {
    if (!input.trim() || sending) return

    const userMessage = input.trim()
    setInput('')
    setSending(true)

    const userMsg = {
      id: `user-${Date.now()}`,
      content: userMessage,
      isUser: true,
    }
    setMessages(prev => [...prev, userMsg])

    try {
      let activeThreadId = currentThread?.id

      if (!activeThreadId) {
        const contentIds = attachedContents.map(c => c.content_id || c.id)
        const res = await createChatThread({
          name: userMessage.slice(0, 50),
          contentIds,
          question: userMessage,
        })
        activeThreadId = res.threadId
        setCurrentThread({ id: activeThreadId, name: userMessage.slice(0, 50) })
        navigate(`/chat/${activeThreadId}`, { replace: true })
        loadThreads()
      }

      setStreamingContent('')
      let fullContent = ''

      for await (const chunk of streamChat(activeThreadId, userMessage)) {
        if (chunk.type === 'token' && chunk.token) {
          fullContent += chunk.token
          setStreamingContent(fullContent)
        } else if (chunk.type === 'done' && chunk.response && !fullContent) {
          fullContent = chunk.response
          setStreamingContent(fullContent)
        }
      }

      setMessages(prev => [...prev, {
        id: `ai-${Date.now()}`,
        content: fullContent,
        isUser: false,
      }])
      setStreamingContent('')
    } catch (err) {
      console.error('Chat error:', err)
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        content: `Error: ${err.message}`,
        isUser: false,
      }])
    } finally {
      setSending(false)
    }
  }

  const handleDeleteThread = async (id) => {
    try {
      await deleteChatThread(id)
      setThreads(prev => prev.filter(t => t.id !== id))
      if (currentThread?.id === id) {
        navigate('/chat')
      }
    } catch (err) {
      console.error('Failed to delete thread:', err)
    }
  }

  const handleNewChat = () => {
    navigate('/chat')
    setCurrentThread(null)
    setMessages([])
    setAttachedContents([])
    inputRef.current?.focus()
  }

  const handleContentSelect = (contents) => {
    setAttachedContents(contents)
  }

  const handleRemoveContent = (content) => {
    const id = content.content_id || content.id
    setAttachedContents(prev => prev.filter(c => (c.content_id || c.id) !== id))
  }

  const startEditingName = () => {
    setEditedName(currentThread?.name || '')
    setIsEditingName(true)
  }

  const saveThreadName = async () => {
    if (!editedName.trim() || !currentThread?.id) {
      setIsEditingName(false)
      return
    }
    
    try {
      await updateChatThread(currentThread.id, { name: editedName.trim() })
      setCurrentThread(prev => ({ ...prev, name: editedName.trim() }))
      setThreads(prev => prev.map(t => 
        t.id === currentThread.id ? { ...t, name: editedName.trim() } : t
      ))
    } catch (err) {
      console.error('Failed to rename thread:', err)
    } finally {
      setIsEditingName(false)
    }
  }

  const cancelEditingName = () => {
    setIsEditingName(false)
    setEditedName('')
  }

  return (
    <div className="h-[calc(100vh-3.5rem)] lg:h-screen flex">
      {/* History sidebar - desktop */}
      <div className="hidden lg:flex w-72 flex-col border-r border-border bg-card/50">
        <div className="p-4 border-b border-border">
          <button
            onClick={handleNewChat}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg shadow-primary/25"
          >
            <Plus className="w-4 h-4" />
            New Chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {threads.map((thread) => (
            <ThreadItem
              key={thread.id}
              thread={thread}
              isActive={currentThread?.id === thread.id}
              onClick={() => navigate(`/chat/${thread.id}`)}
              onDelete={handleDeleteThread}
            />
          ))}
        </div>
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-border bg-card/50">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="lg:hidden p-2 rounded-lg hover:bg-accent"
            >
              <History className="w-5 h-5" />
            </button>
            <div className="flex-1 min-w-0">
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveThreadName()
                      if (e.key === 'Escape') cancelEditingName()
                    }}
                    className="px-2 py-1 text-sm font-semibold bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 w-48"
                    autoFocus
                  />
                  <button
                    onClick={saveThreadName}
                    className="p-1 rounded hover:bg-accent text-green-500"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={cancelEditingName}
                    className="p-1 rounded hover:bg-accent text-muted-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 group">
                  <h1 className="font-semibold truncate">
                    {currentThread?.name || 'New Chat'}
                  </h1>
                  {currentThread && (
                    <button
                      onClick={startEditingName}
                      className="p-1 rounded hover:bg-accent opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Rename chat"
                    >
                      <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                  )}
                </div>
              )}
              {currentThread && !isEditingName && (
                <p className="text-xs text-muted-foreground">
                  {messages.length} messages
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleNewChat}
            className="lg:hidden p-2 rounded-lg hover:bg-accent"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : messages.length === 0 && !streamingContent ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center mb-4 shadow-lg shadow-primary/25">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-semibold mb-2">How can I help you?</h2>
              <p className="text-muted-foreground max-w-md">
                Ask me anything about your knowledge base, or start a conversation about any topic.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-6">
                {[
                  'Summarize my recent notes',
                  'What did I save about AI?',
                  'Help me organize my content',
                ].map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => setInput(prompt)}
                    className="px-4 py-2 rounded-full bg-card border border-border hover:border-primary/50 text-sm transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <ChatMessage key={msg.id} message={msg} isUser={msg.isUser} />
              ))}
              {streamingContent && (
                <ChatMessage
                  message={{ content: streamingContent }}
                  isUser={false}
                />
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Attached Contents */}
        {attachedContents.length > 0 && (
          <AttachedContents
            contents={attachedContents}
            onRemove={handleRemoveContent}
            onAdd={() => setShowContentPicker(true)}
          />
        )}

        {/* Input */}
        <div className="p-4 border-t border-border bg-card/50">
          <div className="max-w-3xl mx-auto flex items-end gap-2">
            <button
              onClick={() => setShowContentPicker(true)}
              className={`p-3 rounded-xl transition-colors ${
                attachedContents.length > 0
                  ? 'bg-primary/10 text-primary'
                  : 'hover:bg-accent text-muted-foreground hover:text-foreground'
              }`}
              title="Attach content"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                placeholder={attachedContents.length > 0 ? "Ask about attached content..." : "Type your message..."}
                rows={1}
                className="w-full px-4 py-3 pr-12 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none transition-all outline-none"
                style={{ minHeight: '48px', maxHeight: '200px' }}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="p-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-primary/25"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Content Picker Modal */}
      <ContentPickerModal
        isOpen={showContentPicker}
        onClose={() => setShowContentPicker(false)}
        onSelect={handleContentSelect}
        selectedIds={attachedContents.map(c => c.content_id || c.id)}
      />

      {/* Mobile history drawer */}
      {showHistory && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setShowHistory(false)}
          />
          <div className="fixed inset-y-0 left-0 w-72 bg-card border-r border-border z-50 lg:hidden animate-slide-in">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold">Chat History</h2>
              <button
                onClick={() => setShowHistory(false)}
                className="p-1.5 rounded-lg hover:bg-accent"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-3">
              <button
                onClick={() => {
                  handleNewChat()
                  setShowHistory(false)
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground mb-3"
              >
                <Plus className="w-4 h-4" />
                New Chat
              </button>
            </div>
            <div className="overflow-y-auto p-3 space-y-1">
              {threads.map((thread) => (
                <ThreadItem
                  key={thread.id}
                  thread={thread}
                  isActive={currentThread?.id === thread.id}
                  onClick={() => {
                    navigate(`/chat/${thread.id}`)
                    setShowHistory(false)
                  }}
                  onDelete={handleDeleteThread}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
